import { useTheme } from "@emotion/react";
import { ExpandMore, Phone } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    Link,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useMicrospecThemeLanguageContext } from "../Themes";

const LoginPhoneIdentifier = ({
    identifier,
    error,
    loading,
    signIn,
    redirectTo,
    enableRegister = true,
    enabledCountryCodes = [],
    registerMode = false,
    onIdentifierChange,
    onSubmit,
    onSetCountryCode
}) => {
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const theme = useTheme();

    // Local state for country code selection
    const [selectedCountryCode, setSelectedCountryCode] = useState(enabledCountryCodes[0]);

    // Generate gradients from theme colors
    const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

    // Set initial country code
    useEffect(() => {
        onSetCountryCode(selectedCountryCode.label);
    }, [onSetCountryCode, selectedCountryCode.label]);

    const handleCountryCodeChange = (event) => {
        const countryCode = enabledCountryCodes.find(country => country.code === event.target.value);
        setSelectedCountryCode(countryCode);
        // Notify parent component about country code change
        onSetCountryCode(countryCode.label);
    };

    return (
        <Box component="form" onSubmit={onSubmit} noValidate  >
            {/* Phone Input with Country Code */}
            <Box sx={{ display: 'flex', gap: 1, mt: 5 }} dir="ltr" >
                {/* Country Code Dropdown */}
                <FormControl
                    sx={{
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: `0 4px 20px ${theme.palette.primary.main}20`
                            },
                            '&.Mui-focused': {
                                boxShadow: `0 4px 20px ${theme.palette.primary.main}30`
                            }
                        }
                    }}

                >
                    <Select
                        value={selectedCountryCode.code}
                        onChange={handleCountryCodeChange}
                        displayEmpty
                        IconComponent={ExpandMore}
                        sx={{
                            '& .MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 1.6
                            }
                        }}
                    >
                        {enabledCountryCodes.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography component="span" sx={{ fontSize: '1.2rem' }}>
                                        {country.flag}
                                    </Typography>
                                    <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                        {country.label}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Phone Number Input */}
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="identifier"
                    name="identifier"
                    label={localizationProvider.translateByType({
                        type: "Internal",
                        text: "phoneNumber"
                    })}
                    type="tel"
                    autoComplete="tel"
                    autoFocus
                    dir="rtl"
                    variant="outlined"
                    value={identifier}
                    onChange={onIdentifierChange}
                    sx={{
                        mt: 0,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: `0 4px 20px ${theme.palette.primary.main}20`
                            },
                            '&.Mui-focused': {
                                boxShadow: `0 4px 20px ${theme.palette.primary.main}30`
                            }
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Phone color="action" />
                            </InputAdornment>
                        ),
                    }}
                    placeholder="50-123-4567"
                />
            </Box>

            <Typography
                variant="body2"
                sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.85rem',
                }}
            >
                {localizationProvider.translateByType({
                    type: "Internal",
                    text: "newAccountAutoCreationNoticeForPhone"
                })}
            </Typography>

            {error && (
                <Alert
                    severity='error'
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: '1px solid rgba(211, 47, 47, 0.2)'
                    }}
                >
                    {typeof error === 'object' && error.message ? (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {error.message}
                            </Typography>
                            {error.showLoginLink && (
                                <Link
                                    href={redirectTo ? "/login?redirectTo=" + redirectTo : "/login"}
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    {localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "signIn"
                                    }) || "Sign In"}
                                </Link>
                            )}
                            {error.showRegisterLink && enableRegister && (
                                <Link
                                    href={registerMode ?
                                        (redirectTo ? "/login?redirectTo=" + redirectTo : "/login") :
                                        (redirectTo ? "/register?redirectTo=" + redirectTo : "/register")
                                    }
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    {registerMode ?
                                        (localizationProvider.translateByType({
                                            type: "Internal",
                                            text: "signIn"
                                        }) || "Sign In") :
                                        (localizationProvider.translateByType({
                                            type: "Internal",
                                            text: "signUp"
                                        }) || "Sign Up")
                                    }
                                </Link>
                            )}
                        </Box>
                    ) : (
                        error || localizationProvider.translateByType({
                            type: "Internal",
                            text: "loginError"
                        }) || "خطأ في تسجيل الدخول، يرجى المحاولة مرة أخرى"
                    )}
                </Alert>
            )}

            <FormControlLabel
                control={
                    <Checkbox
                        name="remember"
                        id="remember"
                        defaultValue={true}
                        value={true}
                        sx={{
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                                color: theme.palette.primary.main,
                            },
                        }}
                    />
                }
                label={localizationProvider.translateByType({
                    type: "Internal",
                    text: "rememberMe"
                })}
            />

            <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !identifier.trim()}
                sx={{
                    mt: 2,
                    mb: 3,
                    py: 1.5,
                    borderRadius: 3,
                    background: primaryGradient,
                    boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 12px 40px ${theme.palette.primary.main}40`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                    },
                    '&:disabled': {
                        background: 'rgba(0, 0, 0, 0.12)',
                        transform: 'none',
                        boxShadow: 'none'
                    }
                }}
            >
                {loading ? localizationProvider.translateByType({
                    type: "Internal",
                    text: "loggingInProgress"
                }) : signIn}
            </Button>

            <Grid container spacing={8} sx={{ justifyContent: 'space-between' }}>
                <Grid item xs={6}>
                    <Link
                        textAlign={"end"}
                        href={redirectTo ? "/forgot?redirectTo=" + redirectTo : "/forgot"}
                        variant="body2"
                        sx={{
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            fontWeight: 500,
                            transition: 'color 0.3s ease',
                            '&:hover': {
                                color: theme.palette.primary.dark,
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        <Typography>
                            {localizationProvider.translateByType({
                                type: "Internal",
                                text: "forgotPassword"
                            })}
                        </Typography>
                    </Link>
                </Grid>
                {enableRegister && (
                    <Grid item xs={6}>
                        <Link
                            textAlign={"start"}
                            href={registerMode ?
                                (redirectTo ? "/login?redirectTo=" + redirectTo : "/login") :
                                (redirectTo ? "/register?redirectTo=" + redirectTo : "/register")
                            }
                            variant="body2"
                            sx={{
                                textDecoration: 'none',
                                fontWeight: 500,
                                transition: 'color 0.3s ease',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            <Typography>
                                {registerMode ?
                                    (localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "signIn"
                                    }) || "Sign In") :
                                    (localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "createAccount"
                                    }) || "Create Account")
                                }
                            </Typography>
                        </Link>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default LoginPhoneIdentifier;